/**
 * The `last_seq` the trading-stream client resumes from (SPEC §7.1), as a pure function of the
 * current value and each received frame.
 *
 * - Ordered frames raise it (a replayed older frame never lowers it).
 * - A `Snapshot` sets it to the snapshot's seq (the gateway's last seq), **even when that is lower**:
 *   a restarted gateway under the same session id numbers from 1 again, and keeping the old, higher
 *   seq would make the next Resume skip that incarnation's frames — orders cancelled or filled in
 *   between then stayed "working" on the chart, the DOM and the Orders table.
 */
export function nextLastSeq(
  current: bigint,
  env: { seq: bigint; body: { case: string | undefined } },
): bigint {
  if (env.body.case === "snapshot") return env.seq;
  return env.seq > current ? env.seq : current;
}

/**
 * The `last_seq` to send in `Resume` after a `Welcome` (SPEC §7.1, CB6). Our `last_seq` means
 * something only to the gateway process and session that numbered it: a different session, or a
 * different `boot_id` (the gateway restarted; the session id is stable per user, and the new
 * process numbers from 1 again, so its seq can overtake ours and a Resume would silently skip
 * part of its history), resumes from 0 — the new process's ring start, or a Snapshot.
 * `prev.sessionId`/`prev.bootId` are null before the first Welcome.
 */
export function resumeFromSeq(
  prev: { sessionId: string | null; bootId: string | null; lastSeq: bigint },
  welcome: { sessionId: string; bootId: string },
): bigint {
  if (prev.sessionId !== null && prev.sessionId !== welcome.sessionId) return 0n;
  if (prev.bootId !== null && prev.bootId !== welcome.bootId) return 0n;
  return prev.lastSeq;
}

/** Display catch-up starts from an applied cursor only in the same known server incarnation.
 * A lower server cursor means restart/reset even if an older server reused its boot identity. */
export function replayAppliedStart(
  prev: { sessionId: string | null; bootId: string | null; appliedSeq: bigint },
  welcome: { sessionId: string; bootId: string; lastSeq: bigint },
): bigint {
  return prev.sessionId === welcome.sessionId &&
    prev.bootId !== null &&
    prev.bootId !== "" &&
    prev.bootId === welcome.bootId &&
    prev.appliedSeq <= welcome.lastSeq
    ? prev.appliedSeq
    : 0n;
}

/** Advance the applied prefix, not the maximum seen sequence. Live frames may precede replay.
 * Full snapshots cover every sequence through their cursor. Call only after all listeners apply. */
export function nextAppliedSeq(
  current: bigint,
  env: { seq: bigint; body: { case: string | undefined } },
  pending: Set<bigint>,
): bigint {
  let applied = current;
  if (env.body.case === "snapshot") {
    applied = env.seq;
    for (const seq of pending) if (seq <= applied) pending.delete(seq);
  } else if (env.seq > applied) pending.add(env.seq);
  while (pending.delete(applied + 1n)) applied++;
  return applied;
}
